import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { getSingleValidateItemCounter } from "api/layer";
import FathomIcon from "components/fathomIcon/fathomIcon";
import Svg from "components/svg/svg";
import { RightPanel } from "helpers/constants";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./fathom-popup.module.scss";
import {
  buildClimateRiskScores,
  buildFutureHazardRiskScores,
  buildCrsComponentRiskScores,
  CRS_RESPONSE_STRUCTURE,
  filterFathomLayers,
  filterNonFathomLayers,
} from "../shared/analysis-shared";
import {
  executeSingleAnalysisWithReporting,
  syncSingleAnalysisRunningStatus,
  SingleAnalysisParams,
} from "../shared/single-analysis-executor";

/* 弹窗打开时拉取单点扣费预估 */
export default function SingleFathomPopup() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const [charging, setCharging] = useState(0);
  const [usedPrcentage, setUsedPrcentage] = useState(0); // 0: <80%  1: ≥80%  2: 超配额
  const [donotAskAgain, setDonotAskAgain] = useState(false);

  const isOpen =
    !!context.singleFathomPopup &&
    context.rightPanel === RightPanel.Single;

  const onClose = () => {
    dispatch({ type: AppAction.setSingleFathomPopup, payload: undefined });
  };

  const selectedButtonArray = context.selectLayer;

  const runAnalysis = async (selectedButtonsParam: any[]) => {
    dispatch({ type: AppAction.setAskAgain, payload: donotAskAgain });

    const selectedButtonsRaw = selectedButtonsParam?.length ? selectedButtonsParam : selectedButtonArray;
    if (!selectedButtonsRaw?.length) return;

    const selectedButtons: string[][] = [
      selectedButtonsRaw[0] || [],
      selectedButtonsRaw[1] || [],
      selectedButtonsRaw[2] || [],
      selectedButtonsRaw[3] || [],
    ];
    dispatch({ type: AppAction.setSingleAnalysisSelectedLayers, payload: selectedButtons });

    const params: SingleAnalysisParams = {
      eventLayers: [],
      catNetLayers: [],
      responseStructure: [],
      climateRiskScores: [],
      longitude: undefined,
      latitude: undefined,
      country: undefined,
      oversea: undefined,
      spatial: undefined,
    };

    if (context.currentMultipleLocationInfo) {
    params.longitude = context.currentMultipleLocationInfo.longitudeWgs;
    params.latitude = context.currentMultipleLocationInfo.latitudeWgs;
    params.country = context.currentMultipleLocationInfo.country;
      params.spatial = "wgs";
    } else {
      params.longitude = context.searchLocationInfo?.[context.language]?.location?.split(",")[0];
      params.latitude = context.searchLocationInfo?.[context.language]?.location?.split(",")[1];
      params.country = context.searchLocationInfo?.[context.language]?.country;
      params.oversea = context.searchLocationInfo?.[context.language]?.oversea;
      params.spatial = context.searchLocationInfo?.[context.language]?.spatial;
    }

    if (selectedButtons[0].length) {
      params.catNetLayers = selectedButtons[0];
    }

    if (selectedButtons[1].length) {
      params.climateRiskScores = buildClimateRiskScores(selectedButtons[1]);
      params.responseStructure = [CRS_RESPONSE_STRUCTURE];
    }

    if (selectedButtons[2].length) {
      params.futureHazardRiskScores = buildFutureHazardRiskScores(selectedButtons[2]);
      params.responseStructure = [CRS_RESPONSE_STRUCTURE];
    }

    if (selectedButtons[3].length) {
      params.crsComponentRiskScores = buildCrsComponentRiskScores(selectedButtons[3]);
      params.responseStructure = [CRS_RESPONSE_STRUCTURE];
    }

    syncSingleAnalysisRunningStatus(
      selectedButtons,
      dispatch,
      context.climateRiskCategorySspData
    );
    executeSingleAnalysisWithReporting(params, selectedButtons, context.hazardLegends, dispatch);

    dispatch({ type: AppAction.setSingleLocationAnalysed, payload: true });
    dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: false });
    onClose();
  };

  const handelNo = () => {
    dispatch({ type: AppAction.setAskAgain, payload: donotAskAgain });
    if (!selectedButtonArray || !selectedButtonArray.length) return;

    const hasOtherElements = filterNonFathomLayers(selectedButtonArray[0]).length > 0;
    if (hasOtherElements) {
      const selectedButtonParam = filterNonFathomLayers(selectedButtonArray[0]);
      dispatch({
        type: AppAction.setSelectLayer,
        payload: [
          selectedButtonParam,
          selectedButtonArray[1] || [],
          selectedButtonArray[2] || [],
          selectedButtonArray[3] || [],
        ],
      });
      runAnalysis([
        selectedButtonParam,
        selectedButtonArray[1] || [],
        selectedButtonArray[2] || [],
        selectedButtonArray[3] || [],
      ]);
    } else {
      dispatch({ type: AppAction.setSingleAnalyseModal, payload: { type: "1" } });
      onClose();
    }
  };

  /* 弹窗打开时拉取单点扣费预估 */
  useEffect(() => {
    if (!isOpen) return;
    if (!selectedButtonArray || !selectedButtonArray.length) return;

    const params: any = {
      eventLayers: [],
      catNetLayers: [],
      responseStructure: [],
      climateRiskScores: [],
    };

    if (selectedButtonArray[0].length) params.catNetLayers = selectedButtonArray[0];
    if (selectedButtonArray[1].length) {
      params.climateRiskScores = selectedButtonArray[1].map((item: any) => ({ index: item }));
      params.responseStructure = [CRS_RESPONSE_STRUCTURE];
    }
    if (context.selectedEvent) params.eventLayers = [context.selectedEvent.layerId];

    if (context.currentMultipleLocationInfo) {
    params.longitude = context.currentMultipleLocationInfo.longitudeWgs;
    params.latitude = context.currentMultipleLocationInfo.latitudeWgs;
    params.country = context.currentMultipleLocationInfo.country;
      params.spatial = "wgs";
    } else {
      params.longitude = context.searchLocationInfo?.[context.language]?.location?.split(",")[0];
      params.latitude = context.searchLocationInfo?.[context.language]?.location?.split(",")[1];
      params.country = context.searchLocationInfo?.[context.language]?.country;
      params.oversea = context.searchLocationInfo?.[context.language]?.oversea;
      params.spatial = context.searchLocationInfo?.[context.language]?.spatial;
    }

    getSingleValidateItemCounter(params).then((res) => {
      if (res.data.itemFathomQuotas && res.data.itemFathomQuotas > 0) {
        if ((res.data.trItemFathomCount + (res.data.notExistFathomCount ?? 0)) > res.data.itemFathomQuotas) {
          setUsedPrcentage(2);
        } else if (res.data.trItemFathomCount / res.data.itemFathomQuotas >= 0.8) {
          setUsedPrcentage(1);
        } else {
          setUsedPrcentage(0);
        }
      }
      setCharging(res.data.notExistFathomCount ?? 0);
    });
  }, [context.singleFathomPopup]);

  return (
    <Dialog maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle className={styles.title}>{t("fathom.popup.title")}</DialogTitle>
      <DialogContent>
        <div className={styles.content}>
          <div>{t("fathom.popup.text")}</div>
          <div>{t("fathom.popup.charging", { num: charging })}</div>
          {usedPrcentage === 1 && (
            <span dangerouslySetInnerHTML={{ __html: t("fathom.popup.warning") }} />
          )}
          {usedPrcentage === 2 && (
            <span dangerouslySetInnerHTML={{ __html: t("fathom.popup.error") }} />
          )}
        </div>

        {selectedButtonArray?.[0]?.length &&
          filterFathomLayers(selectedButtonArray[0]).map((e: string) => (
            <div key={e} className={styles.item}>
              <div>{Svg({ icon: e })()}</div>
              <div style={{ marginLeft: "6px" }}>{t("layerId." + e)}</div>
              <FathomIcon className={styles.premium} />
            </div>
          ))}

        <div>
          <Checkbox
            color="success"
            onChange={() => setDonotAskAgain(!donotAskAgain)}
            checked={donotAskAgain}
          />
          {t("fathom.popup.ask")}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          color="success"
          variant="outlined"
          onClick={handelNo}
          style={{ margin: "0 10px 10px 0" }}
        >
          {t("buttons.no")}
        </Button>
        <Button
          color="success"
          variant="contained"
          onClick={() => runAnalysis([])}
          style={{ margin: "0 10px 10px 0" }}
          disabled={usedPrcentage === 2}
        >
          {t("buttons.yes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}



